import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { EventTicket, UserRole, EventTicketStatus } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import EventTicketABI from '@/abis/EventTicket.json';
import { ethers, EventLog } from 'ethers';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const StatusPill: React.FC<{ status: EventTicketStatus }> = ({ status }) => (
    <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ 
            status === EventTicketStatus.VALID
                ? 'bg-green-500/20 text-green-300'
                : status === EventTicketStatus.CHECKED_IN
                ? 'bg-blue-500/20 text-blue-300'
                : 'bg-red-500/20 text-red-300' // INVALIDATED
        }`}
    >
        {status}
    </span>
);

const TableActions: React.FC<{ 
  ticket: EventTicket;
  role: UserRole; 
  onAction: (action: string, ticket: EventTicket) => void;
}> = ({ ticket, role, onAction }) => {

  const handleAction = (action: string) => {
    onAction(action, ticket);
  };

  if (role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE) {
    if (!ticket.onChain) {
        return <ActionButton onClick={() => handleAction('mint')} className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/40">Mint</ActionButton>
    }
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('invalidate')} className="bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/40">Invalidate</ActionButton>
          <ActionButton onClick={() => handleAction('burn')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Burn</ActionButton>
        </div>
      );
  }
  
  return <></>;
};

const EventTicketRealDataTable = ({ currentUserRole, currentUserId }: { currentUserRole: UserRole, currentUserId: string }) => {
  const [allTickets, setAllTickets] = useState<EventTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, signer, isConnected } = useWallet();
  const PAGE_SIZE = 10;
  const [isFallback, setIsFallback] = useState(false);


  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsFallback(false);
    addLog("Starting hybrid event ticket data fetch.", LogLevel.INFO, LogCategory.DATA);

    const combinedTickets = new Map<string, EventTicket>();

    // 1. Fetch baseline data from API or Mock
    try {
        const response = await fetch(`${API_BASE_URL}/api/event-tickets`, { credentials: 'include' });
        let apiData;

        if (response.ok) {
            apiData = await response.json();
            addLog(`Loaded ${apiData.length} tickets from REAL API.`, LogLevel.INFO, LogCategory.API);
        } else {
            addLog(`Real API failed, falling back to mock data.`, LogLevel.WARN, LogCategory.API);
            const mockResponse = await fetch('/event-tickets.json');
            if (!mockResponse.ok) throw new Error('Real API and mock data both failed.');
            apiData = await mockResponse.json();
            notify('Displaying mock data. API failed.', 'warn');
        }
        
        if (Array.isArray(apiData)) {
            apiData.forEach(ticket => {
                const id = `${ticket.eventId}-${ticket.ownerId}`;
                combinedTickets.set(id, { ...ticket, id, onChain: false });
            });
            addLog(`Loaded ${combinedTickets.size} tickets from off-chain source.`, LogLevel.INFO, LogCategory.DATA);
        } else {
          throw new Error('Fetched off-chain data is not an array.');
        }
    } catch (e: unknown) {
        const localDataError = `Could not load off-chain ticket data: ${(e as Error).message}`;
        addLog(localDataError, LogLevel.ERROR, LogCategory.API);
        setError(localDataError);
        // We can continue to try on-chain fetch even if local fails
    }

    // 2. If connected, fetch On-Chain Data and merge/update
    if (provider && isConnected) {
      try {
        const ticketContract = new ethers.Contract(contractAddresses.EventTicket, EventTicketABI.abi, provider);
        const transferSingleFilter = ticketContract.filters.TransferSingle();
        const transferBatchFilter = ticketContract.filters.TransferBatch();

        const [singleEvents, batchEvents] = await Promise.all([
            ticketContract.queryFilter(transferSingleFilter),
            ticketContract.queryFilter(transferBatchFilter)
        ]);

        const processOnChainEvent = async (tokenId: bigint, owner: string, quantity: bigint) => {
            const ticketId = `${tokenId.toString()}-${owner}`;
            const checkedIn = await ticketContract.checkedIn(tokenId, owner);
            const status = checkedIn ? EventTicketStatus.CHECKED_IN : EventTicketStatus.VALID;

            const existingTicket = combinedTickets.get(ticketId) || {};
            
            combinedTickets.set(ticketId, {
                ...existingTicket,
                id: ticketId,
                eventId: tokenId.toString(),
                tokenId: tokenId.toString(),
                ownerId: owner,
                quantity: Number(quantity),
                status: status,
                onChain: true,
            });
        };
        
        for (const event of [...singleEvents, ...batchEvents]) {
            const args = (event as EventLog).args;
            if ((event as EventLog).eventName === 'TransferBatch') {
                await Promise.all(args.ids.map((id: bigint, i: number) => processOnChainEvent(id, args.to, args.values[i])));
            } else {
                await processOnChainEvent(args.id, args.to, args.value);
            }
        }
        addLog(`Merged on-chain data. Total tickets: ${combinedTickets.size}`, LogLevel.SUCCESS, LogCategory.DATA);
      } catch (e: unknown) {
        const onChainError = "Could not load on-chain ticket data. Displaying local data only.";
        addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
        setError(onChainError);
        notify(onChainError, 'warn');
        setIsFallback(true);

      }
    } else {
        setIsFallback(true);
    }

    setAllTickets(Array.from(combinedTickets.values()));
    setIsLoading(false);
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

 const handleAction = async (action: string, ticket: EventTicket) => {
    if (!signer) {
      notify('Please connect your wallet to perform actions.', 'error');
      return;
    }

    addLog(`Admin ${currentUserId} initiated '${action}' on ticket ${ticket.id}`, LogLevel.INFO, LogCategory.EVENT_TICKET);
    const ticketContract = new ethers.Contract(contractAddresses.EventTicket, EventTicketABI.abi, signer);
    const tokenId = BigInt(ticket.eventId); // Use eventId as the tokenId for contract calls

    if (action === 'mint') {
        if (!window.confirm(`Mint ticket for event ${ticket.eventId} and send to ${ticket.ownerId}?`)) return;
        try {
            // Assuming a mint function exists on the contract that takes owner, eventId, and quantity
            const tx = await ticketContract.mint(ticket.ownerId, tokenId, ticket.quantity);
            await tx.wait();
            notify(`Ticket for event ${ticket.eventId} successfully minted to ${ticket.ownerId}.`, 'success');
            fetchData();
        } catch(err) {
            notify(`Failed to mint ticket: ${(err as Error).message}`, 'error');
            addLog(`Mint failed for ticket ${ticket.id}`, LogLevel.ERROR, LogCategory.EVENT_TICKET, { error: (err as Error).message });
        }
    } else if (action === 'invalidate') {
      if (!ticket.onChain) { notify('Cannot invalidate an off-chain ticket.', 'warn'); return; }
      if (!window.confirm('Are you sure you want to invalidate (check-in) this ticket?')) return;
      try {
        const tx = await ticketContract.checkInTicket(ticket.ownerId, tokenId);
        await tx.wait();
        notify(`Ticket ${ticket.id} successfully invalidated.`, 'success');
        fetchData();
      } catch (err) {
        notify(`Failed to invalidate ticket: ${(err as Error).message}`, 'error');
      }
    } else if (action === 'burn') {
        if (!ticket.onChain) { notify('Cannot burn an off-chain ticket.', 'warn'); return; }
      if (!window.confirm('Are you sure you want to BURN this ticket? This action is irreversible.')) return;
      try {
        const tx = await ticketContract.burn(ticket.ownerId, tokenId, ticket.quantity);
        await tx.wait();
        notify(`Ticket ${ticket.id} was successfully burned.`, 'success');
        fetchData();
      } catch (err: unknown) {
          notify((err as Error).message || 'An error occurred while burning the ticket.', 'error');
      }
    }
  };
  
  const filteredTickets = useMemo(() => {
    return allTickets.filter(t => 
      (t.event?.name || t.eventId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.owner?.name || t.ownerId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.status.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allTickets, searchTerm]);

  const displayedTickets = useMemo(() => {
      return filteredTickets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredTickets, page]);

  const hasMore = page * PAGE_SIZE < filteredTickets.length;

  if (isLoading && allTickets.length === 0) return (
    <div className="flex justify-center items-center h-48">
        <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
        <p className="ml-4 text-kala-400">Loading ticket data...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Event Ticket Management</h2>
            <div className="flex items-center space-x-4">
                <div className="relative">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                    <input
                        type="text"
                        placeholder="Search by event, owner, or status..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
                    />
                </div>
            </div>
        </div>

        {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
        {isFallback ? (
            <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
            Note: Wallet disconnected. On-chain data is unavailable.
            </div>
        ) : (
            <div className="p-2 mb-4 bg-green-500/10 text-green-400 text-center text-[10px] uppercase tracking-widest border-y border-green-700">
            Displaying live on-chain data.
            </div>
        )}


      <Table>
        <TableCaption>A comprehensive list of all event tickets.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Event</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Ticket ID</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedTickets.map((ticket) => (
            <TableRow key={ticket.id} className={`${!ticket.onChain ? 'bg-purple-900/20' : ''}`}>
              <TableCell>{ticket.event?.name || ticket.eventId}</TableCell>
              <TableCell className="truncate max-w-xs">{ticket.owner?.name || ticket.ownerId}</TableCell>
              <TableCell>{ticket.tokenId}</TableCell>
              <TableCell>{ticket.quantity}</TableCell>
              <TableCell><StatusPill status={ticket.status} /></TableCell>
              <TableCell className="text-right">
                  <TableActions ticket={ticket} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

       {displayedTickets.length === 0 && !isLoading && (
        <div className="text-center p-8 text-kala-500">
            <p>No tickets found.</p>
        </div>
      )}

      <div className="flex justify-between items-center mt-6">
        <button 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
            Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredTickets.length / PAGE_SIZE)}</span>
        <button 
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
            Next
        </button>
      </div>
    </div>
  );
};

export default EventTicketRealDataTable;
